import { useEffect, useState, useCallback } from "react";
import { Loader2, Link2, Link2Off, PlugZap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const HEALTH_COLOR = "#8FB9B2";
const inputCls =
  "w-full px-3 py-2 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200";

function ConnectionCard({ conn, onChanged }) {
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    for (const f of conn.credential_fields) {
      if (!creds[f.key]) return toast.error(`${f.label} is required`);
    }
    setSaving(true);
    try {
      await api.put(`/health/connections/${conn.manufacturer}`, { credentials: creds });
      toast.success(`${conn.name} connected`);
      setCreds({});
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/health/connections/${conn.manufacturer}/test`);
      if (data.success) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await api.delete(`/health/connections/${conn.manufacturer}`);
      toast.success(`${conn.name} disconnected`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid={`connection-card-${conn.manufacturer}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <PlugZap className="w-5 h-5 text-muted-foreground" strokeWidth={1.75} />
          <h2 className="font-serif text-xl text-foreground">{conn.name}</h2>
        </div>
        {conn.connected ? (
          <span className="flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-full text-emerald-700 bg-emerald-50" data-testid={`connection-status-${conn.manufacturer}`}>
            <CheckCircle2 className="w-4 h-4" /> Connected
          </span>
        ) : (
          <span className="text-sm text-muted-foreground px-2.5 py-1 rounded-full bg-secondary/60" data-testid={`connection-status-${conn.manufacturer}`}>
            Not connected
          </span>
        )}
      </div>

      <form onSubmit={save} className="space-y-3">
        {conn.credential_fields.map((f) => (
          <div key={f.key}>
            <label className="block text-xs text-muted-foreground mb-1">{f.label}</label>
            <input
              type={f.type === "password" ? "password" : f.type === "email" ? "email" : "text"}
              className={inputCls}
              value={creds[f.key] || ""}
              onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
              placeholder={conn.connected ? "•••••••• (leave to keep, re-enter to update)" : f.label}
              data-testid={`connection-field-${conn.manufacturer}-${f.key}`}
            />
          </div>
        ))}
        <p className="text-xs text-muted-foreground">Credentials are encrypted before they are stored.</p>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-[#2C2C2C] disabled:opacity-50" style={{ backgroundColor: HEALTH_COLOR }} data-testid={`connection-save-${conn.manufacturer}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} {conn.connected ? "Update" : "Connect"}
          </button>
          {conn.connected && (
            <>
              <button type="button" onClick={test} disabled={testing} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border border-input bg-white/70 hover:border-foreground/40 disabled:opacity-50 transition-colors duration-200" data-testid={`connection-test-${conn.manufacturer}`}>
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Test connection
              </button>
              <button type="button" onClick={disconnect} disabled={disconnecting} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm text-muted-foreground hover:text-destructive disabled:opacity-50" data-testid={`connection-disconnect-${conn.manufacturer}`}>
                {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2Off className="w-4 h-4" />} Disconnect
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default function HealthConnections() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/health/connections");
      setConnections(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="health-connections">
      <p className="text-sm text-muted-foreground">
        Connect a manufacturer account so devices can sync real readings. Simulated ("Generic") devices
        do not need a connection.
      </p>
      {connections.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No connectable manufacturers.</p>
      ) : (
        connections.map((c) => <ConnectionCard key={c.manufacturer} conn={c} onChanged={load} />)
      )}
    </div>
  );
}
